<template>
  <div>
    <ValidationObserver ref="observer" v-slot="{ invalid, dirty }">
      <!-- Qualification Form Section -->
      <v-row>
        <v-col cols="12" sm="4" class="pb-0 pt-2">
          <v-combobox
            v-model="selected"
            label="* Select lookup type"
            :items="lookupItems"
            @change="resetValidation($event)"
            :disabled="withData"
          ></v-combobox>
        </v-col>
      </v-row>

      <!-- Physical Address Search -->
      <div v-if="selected !== 'Location ID'">
        <v-row v-if="selected === 'Physical Address'">
          <v-col cols="12" class="pt-0 mt-0 pb-0">
            <ValidationProvider
              v-slot="{ errors }"
              name="Search for Address"
              rules="required"
            >
              <v-autocomplete
                :items="addressResults"
                label="Search for physical street Address, not PO Box"
                :search-input.sync="search"
                v-model="selectedAddress"
                item-text="formatted_address"
                item-value="formatted_address"
                @change="completeAddress"
                persistent-hint
                autocomplete="off"
                :error-messages="errors"
                :disabled="disableSearch"
                no-filter
                return-object
              >
              </v-autocomplete>
            </ValidationProvider>
          </v-col>
        </v-row>

        <!-- Geocode Search -->
        <v-row v-if="selected === 'Geocode'">
          <v-col cols="6" class="mt-0 pb-0">
            <ValidationProvider
              v-slot="{ errors }"
              name="Latitude"
              rules="required|between:-180.999999,180.999999"
            >
              <v-text-field
                label="Latitude"
                v-model="latitude"
                outlined
                :error-messages="errors"
                autocomplete="off"
              ></v-text-field>
            </ValidationProvider>
          </v-col>
          <v-col cols="6" class="mt-0 pb-0">
            <ValidationProvider
              v-slot="{ errors }"
              name="Longitude"
              rules="required|between:-180.999999,180.999999"
            >
              <v-text-field
                label="longitude"
                v-model="longitude"
                outlined
                :error-messages="errors"
                autocomplete="off"
              ></v-text-field>
            </ValidationProvider>
          </v-col>
        </v-row>

        <!-- Submit/Clear Buttons -->
        <v-row v-if="showSubmit">
          <v-col class="pt-0 pb-0">
            <v-btn
              :disabled="invalid || !dirty || withData"
              class="primary mr-5"
              @click="submitAddressSearch()"
            >Submit</v-btn>
            <v-btn
              :disabled="invalid || !dirty || withData"
              @click="clear()"
            >Clear</v-btn>
          </v-col>
        </v-row>
      </div>
    </ValidationObserver>

    <!-- Location ID Selection Section -->
    <v-row v-if="showLocIdInput" class="mt-6">
      <v-col cols="12" class="pb-0" v-if="!hasMatch">
        <p class="red--text mb-0">No matching addresses found.</p>
      </v-col>

      <v-col
        cols="12"
        sm="9"
        class="pb-0"
        v-if="hasMatch"
      >
        <ValidationProvider
          v-slot="{ errors }"
          name="Location ID"
          rules="required"
        >
          <v-autocomplete
            v-if="queryAddress.length > 1"
            outlined
            :items="sortedQueryAddress"
            label="Select Location ID"
            v-model="selectedLocationId"
            :item-text="locationText"
            item-value="locationId"
            :error-messages="errors"
            @change="updateLocationId"
            @click:clear="clearLocId()"
          >
          </v-autocomplete>
          <v-text-field
            label="Location ID"
            v-model="selectedLocationId"
            @change="updateLocationId"
            outlined
            :error-messages="errors"
            autocomplete="off"
            maxlength="15"
          ></v-text-field>
        </ValidationProvider>
      </v-col>

      <v-col cols="12" sm="3" class="pt-0" v-if="hasMatch">
        <v-btn
          class="primary mt-2"
          width="100%"
          large
          :disabled="!selectedLocationId"
          @click="runServiceQualification()"
        >Run NBN SQ</v-btn>
      </v-col>
    </v-row>

    <!-- Service Qualification Results -->
    <div v-if="showSqResult" class="mt-6">
      <div
        class="custom-div"
        :class="isServiceable ? 'serviceable' : 'not-serviceable'"
      >
        <h3 class="mb-4">NBN Service Qualification Result</h3>
        <v-btn @click="copyResults" class="primary mb-4">
          Copy Result
        </v-btn>
        
        <div ref="results">
          <!-- Address Information -->
          <div class="result-row">
            <span class="result-label">Address:</span>
            <span class="result-value">{{ formattedAddress }}</span>
          </div>

          <div class="result-row">
            <span class="result-label">NBN Co Location ID:</span>
            <span class="result-value">{{ selectedLocationId }}</span>
          </div>

          <div class="result-row">
            <span class="result-label">Service Class:</span>
            <span class="result-value">{{ serviceClass }}</span>
          </div>

          <div class="result-row">
            <span class="result-label">Primary Access Technology:</span>
            <span class="result-value">{{ accessTechnology }}</span>
          </div>

          <div class="result-row">
            <span class="result-label">CSA Id:</span>
            <span class="result-value">{{ csaId }}</span>
          </div>

          <div class="result-row">
            <span class="result-label">Serviceability Status:</span>
            <span class="result-value">{{ serviceabilityStatus }}</span>
          </div>

          <!-- Resource Information -->
          <!-- <div v-if="sqNbnResult && sqNbnResult.sq && sqNbnResult.sq.siteRestriction.supportingResource" class="mt-4">
            <div
              v-for="(resource, index) in sqNbnResult.sq.siteRestriction.supportingResource"
              :key="index"
              class="resource-section mt-3 pt-3"
            >
              <div v-if="resource.id" class="result-row">
                <span class="result-label">{{ hasNtd ? 'NTD ID:' : 'Copper Pair ID:' }}</span>
                <span class="result-value">{{ resource.id }}</span>
              </div>
            </div>
          </div> -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "QualificationAggregateNbn",
  props: {
    showButtons: { type: Boolean, default: true },
    withData: { type: Boolean, default: false }
  },
  data: () => ({
    // Form data
    lookupItems: [
      "Physical Address",
      "Location ID", 
      "Geocode",
      "Enhanced SQ - Service Transfer",
      "Enhanced SQ - Service Transition"
    ],
    selected: "Physical Address",
    search: null,
    selectedAddress: null,
    latitude: "",
    longitude: "",
    gnafId: "",
    
    // Address results
    queryAddress: [],
    selectedLocationId: "",
    hasMatch: true,
    showLocIdInput: false,
    
    // Service qualification results - stored locally
    showSqResult: false,
    localSqResult: null, // Store SQ result locally
    localFormattedSQResult: null, // Store formatted result locally
    
    // UI state
    sorting: -1
  }),

  methods: {
    async submitAddressSearch() {
      let payload = {};
      let data = {};
      let methodType = "";

      try {
        switch (this.selected) {
          case "Physical Address":
            methodType = "PHYSICAL_ADDRESS";
            data = {
              StateOrProvince: this.fullAddress.state,
              Postcode: this.fullAddress.postcode,
              Suburb: this.fullAddress.suburb,
              StreetName: this.fullAddress.street_name,
              StreetType: this.fullAddress.street_type,
              StreetNrFirst: this.fullAddress.street_number
            };
            break;
          case "Geocode":
            methodType = "GEOCODE";
            data = {
              Longitude: this.longitude,
              Latitude: this.latitude
            };
            break;
          case "GNAF ID":
            methodType = "GNAF_ID";
            data = { Gnafld: this.gnafId };
            break;
        }

        payload = {
          addressCheckServiceType: "NBN",
          addressCheckMethodType: methodType,
          addressValue: data
        };

        const response = await this.nbnAddressQuery(payload);
        if (response.data) {
          this.setAddressResults(response.data);
          this.showLocIdInput = true;
        }
      } catch (error) {
        console.error("Address search failed:", error);
      }
    },

    async completeAddress(address) {
      if (!address) return;
      
      try {
        await this.getFullAddress();
        this.submitAddressSearch();
      } catch (error) {
        console.error("Address completion failed:", error);
      }
    },

    async runServiceQualification() {
      if (!this.selectedLocationId) return;

      try {
        const payload = {
          addressCheckMethodType: "NBN_LOCATION_ID",
          addressValue: { LocationId: this.selectedLocationId }
        };

        const response = await this.serviceQualificationNbn(payload);
        
        if (response.data) {
          // Store results locally instead of in Vuex store
          this.localSqResult = response.data;
          this.localFormattedSQResult = this.formatSQResult(response.data);
          this.showSqResult = true;

          // Emit the complete result to parent
          this.emitQualificationResult();
        }
      } catch (error) {
        console.error("Service qualification failed:", error);
      }
    },

    // Format the SQ result locally
    formatSQResult(sqData) {
      if (!sqData || !sqData.sq) return null;

      return {
        serviceabilityClass: sqData.sq.siteRestriction.supportingTechnology.serviceabilityClass || "",
        accessTechnology: sqData.sq.siteRestriction.supportingTechnology.primaryAccessTechnology || "",
        csaid: sqData.sq.siteRestriction.supportingRelatedSiteBoundaries?.CSAId || "",
        serviceabilityStatus: sqData.sq.siteRestriction.serviceabilityStatus || ""
      };
    },

    setAddressResults(results) {
      this.queryAddress = this.reformatAddressResults(results);
      this.hasMatch = this.queryAddress.length > 0;
      
      if (this.queryAddress.length === 1) {
        this.selectedLocationId = this.queryAddress[0].locationId;
      }
    },

    reformatAddressResults(results) {
      return results.map(address => ({
        addressSiteName: address.addressSiteName,
        streetNrFirst: address.roadNumber1,
        streetNrLast: address.roadNumber2 || "",
        streetName: address.roadName,
        streetType: address.roadTypeCode,
        suburb: address.localityName,
        postcode: address.postcode,
        stateOrProvince: address.stateTerritoryCode,
        longitude: address.longitude,
        latitude: address.latitude,
        locationId: address.id,
        formattedAddress: address.formattedAddress,
        lotNumber: address.lotNumber || ""
      }));
    },

    updateLocationId() {
      if (this.selectedLocationId) {
        this.resetResults();
      }
    },

    clearLocId() {
      this.selectedLocationId = "";
      this.resetResults();
    },

    resetValidation(event) {
      this.selected = event;
      this.clear();
      
      if (event === "Location ID" || 
          event === "Enhanced SQ - Service Transfer" || 
          event === "Enhanced SQ - Service Transition") {
        this.showLocIdInput = true;
      } else {
        this.showLocIdInput = false;
      }
    },

    clear() {
      this.search = null;
      this.selectedAddress = null;
      this.latitude = "";
      this.longitude = "";
      this.gnafId = "";
      this.selectedLocationId = "";
      this.queryAddress = [];
      this.resetResults();
      this.$refs.observer.reset();
    },

    resetResults() {
      this.showSqResult = false;
      this.localSqResult = null;
      this.localFormattedSQResult = null;
    },

    copyResults() {
      this.selectText(this.$refs.results);
      document.execCommand("copy");
    },

    selectText(element) {
      if (document.selection) {
        const range = document.body.createTextRange();
        range.moveToElementText(element);
        range.select();
      } else if (window.getSelection) {
        const range = document.createRange();
        range.selectNode(element);
        window.getSelection().removeAllRanges();
        window.getSelection().addRange(range);
      }
    },

    locationText(val) {
      if (val.addressSiteName) {
        return `${val.formattedAddress} (${val.addressSiteName} - ${val.locationId})`;
      } else {
        return `${val.formattedAddress} (${val.locationId})`;
      }
    },

    emitQualificationResult() {
      const qualificationData = {
        locationId: this.selectedLocationId,
        sqResult: this.localSqResult,
        formattedSQResult: this.localFormattedSQResult,
        serviceClass: this.serviceClass,
        accessTechnology: this.accessTechnology,
        serviceabilityStatus: this.serviceabilityStatus,
        isServiceable: this.isServiceable,
        csaId: this.csaId,
        queryAddress: this.queryAddress,
        formattedAddress: this.formattedAddress
      };

      this.$emit('qualificationComplete', qualificationData);
    },

    // Public method for parent component to get current data
    getCurrentQualificationData() {
      return {
        locationId: this.selectedLocationId,
        sqResult: this.localSqResult,
        formattedSQResult: this.localFormattedSQResult,
        serviceClass: this.serviceClass,
        accessTechnology: this.accessTechnology,
        serviceabilityStatus: this.serviceabilityStatus,
        isServiceable: this.isServiceable,
        csaId: this.csaId,
        queryAddress: this.queryAddress,
        formattedAddress: this.formattedAddress
      };
    }
  },

  computed: {
    sortedQueryAddress() {
      return this.queryAddress
        .slice(0)
        .sort((a, b) =>
          parseInt(a.lotNumber) < parseInt(b.lotNumber) ||
          parseInt(a.subUnitNr) < parseInt(b.subUnitNr) ||
          parseInt(a.streetNrFirst) < parseInt(b.streetNrFirst) ||
          parseInt(a.streetName) < parseInt(b.streetName)
            ? this.sorting
            : -this.sorting
        );
    },

    showSubmit() {
      return this.selected === "Geocode" || this.selected === "GNAF ID";
    },

    disableSearch() {
      return this.withData;
    },

    formattedAddress() {
      if (this.localSqResult && this.localSqResult.address) {
        return this.localSqResult.address.formattedAddress || "";
      }
      return "";
    },

    serviceClass() {
      return this.localFormattedSQResult?.serviceabilityClass || "";
    },

    accessTechnology() {
      return this.localFormattedSQResult?.accessTechnology || "";
    },

    serviceabilityStatus() {
      if (this.localSqResult && this.localSqResult.sq) {
        return this.localSqResult.sq.siteRestriction.serviceabilityStatus || "";
      }
      return "";
    },

    isServiceable() {
      return this.serviceabilityStatus !== "Rejected";
    },

    csaId() {
      return this.localFormattedSQResult?.csaid || "";
    },

    hasNtd() {
      if (this.localSqResult && this.localSqResult.sq && this.localSqResult.sq.siteRestriction.supportingResource) {
        return this.localSqResult.sq.siteRestriction.supportingResource.some(
          resource => resource.type === "NTD"
        );
      }
      return false;
    },

    // Use local data instead of store data
    sqNbnResult() {
      return this.localSqResult;
    }
  },

  watch: {
    search: function(val) {
      if (val !== null && val.length > 4) {
        this.searchAddress(val);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.custom-div {
  border: 2px solid #9e9e9e;
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 15px;
  font-size: 15px !important;
}

.serviceable {
  border-color: #2cd375 !important;
}

.not-serviceable {
  border-color: #ee3923 !important;
}

.result-row {
  display: flex;
  margin-bottom: 8px;
  
  .result-label {
    font-weight: bold;
    min-width: 200px;
    margin-right: 10px;
  }
  
  .result-value {
    flex: 1;
  }
}

.resource-section {
  border-top: 1px solid #9e9e9e;
}
</style>